import React from 'react';
import { useNavigate } from 'react-router-dom';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import BuildIcon from '@mui/icons-material/Build';
import CodeIcon from '@mui/icons-material/Code';
import ConstructionIcon from '@mui/icons-material/Construction';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ScreenshotMonitorIcon from '@mui/icons-material/ScreenshotMonitor';
import SyncIcon from '@mui/icons-material/Sync';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

type ArticleEntry = {
  id: string;
  title: string;
  path?: string;
};

type SeriesData = {
  key: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  color: string;
  target: string;
  articles: ArticleEntry[];
  /** 準備中のシリーズに表示する予告コンテンツ */
  preview?: string[];
};

const seriesData: SeriesData[] = [
  {
    key: 'preparation',
    icon: <ConstructionIcon />,
    title: '事前準備編',
    description: '必要なツール・環境を全てインストールし、正常に動作することを確認する',
    color: '#546e7a',
    target: '全員',
    articles: [{ id: '0', title: '環境構築', path: '/devdocs/handson/preparation' }],
  },
  {
    key: 'introduction',
    icon: <RocketLaunchIcon />,
    title: '導入編',
    description: 'ストーリーに沿ってプロジェクトの雛形を作成し、初めてのAI生成を体験する',
    color: '#e65100',
    target: '全員',
    articles: [
      {
        id: '1',
        title: 'ストーリー導入 + プロジェクト雛形作成',
        path: '/devdocs/handson/story-scaffold',
      },
    ],
  },
  {
    key: 'requirements',
    icon: <NoteAltIcon />,
    title: '要件定義編',
    description: 'Steeringルールを活用し、AIと対話しながら要件を固める',
    color: '#1565c0',
    target: '全員',
    articles: [
      {
        id: '2',
        title: 'Steeringルールの書き方',
        path: '/devdocs/handson/steering-rules',
      },
      { id: '3-1', title: 'rfp作成', path: '/devdocs/handson/rfp' },
      { id: '3-2', title: 'proposal/business生成', path: '/devdocs/handson/proposal-business' },
      { id: '3-3', title: 'usecase生成', path: '/devdocs/handson/usecase' },
      { id: '3-4', title: 'product作成', path: '/devdocs/handson/product' },
    ],
  },
  {
    key: 'implementation',
    icon: <CodeIcon />,
    title: '実装編',
    description: '設計書駆動でAIを制御し、スキーマ定義からE2Eテスト完了まで一気通貫で開発する',
    color: '#2e7d32',
    target: 'バックエンドエンジニア',
    articles: [
      {
        id: '4-1',
        title: 'structure/techルールの作成',
        path: '/devdocs/handson/structure-tech',
      },
      {
        id: '4-2',
        title: 'スキーマ定義 + API仕様書の生成',
        path: '/devdocs/handson/schema-api',
      },
      { id: '4-3', title: 'API実装ワークフロー', path: '/devdocs/handson/api-workflow' },
      { id: '4-4', title: 'E2Eテスト', path: '/devdocs/handson/e2e' },
    ],
  },
  {
    key: 'frontend',
    icon: <ScreenshotMonitorIcon />,
    title: 'フロントエンド編',
    description: 'API仕様書からUIまで、フロントエンド開発の全工程をAIと進める',
    color: '#6a1b9a',
    target: 'フロントエンドエンジニア',
    articles: [
      {
        id: '5-1',
        title: 'プロジェクト作成 + Steeringルール設置',
        path: '/devdocs/handson/project-setup',
      },
      { id: '5-2', title: 'API仕様書の取り込み', path: '/devdocs/handson/api-specs' },
      { id: '5-3', title: '画面入出力定義書の作成', path: '/devdocs/handson/screen-io' },
      { id: '5-4', title: '画面実装', path: '/devdocs/handson/implementation' },
    ],
  },
  {
    key: 'cicd',
    icon: <SyncIcon />,
    title: 'CICD編（準備中）',
    description:
      'AWSのネットワーク構築からアプリ公開、GitHub Actions連携によるCI/CDパイプライン構築まで。コードをpushするだけで本番にデプロイされる世界を体験する',
    color: '#37474f',
    target: 'インフラエンジニア',
    articles: [
      { id: '6-1', title: 'AWSネットワーク構築（VPC / サブネット / セキュリティグループ）' },
      { id: '6-2', title: 'DNS設定 + ドメイン取得（Route 53）' },
      { id: '6-3', title: 'アプリケーション環境構築（ECS / RDS / ALB）' },
      { id: '6-4', title: 'CI/CDパイプライン構築（GitHub Actions + CodePipeline）' },
    ],
    preview: [
      'VPC・サブネット・セキュリティグループをゼロから設計し、安全なネットワーク基盤を構築する',
      'Route 53でドメインを取得し、HTTPS化まで完了する',
      'ECS（Fargate）+ RDS + ALBでバックエンド・フロントエンドを本番公開する',
      'GitHub Actionsからmainブランチへのマージをフックし、ビルド→デプロイまで自動で走るCI/CDパイプラインを構築する',
      'ここまで作ってきたチームTODOアプリが、実際にインターネット上で動く状態になる',
    ],
  },
];

const HandsonHome: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Stack spacing={3} sx={{ maxWidth: 860, pb: 4 }}>
      {/* パンくず */}
      <Breadcrumbs separator={<NavigateNextIcon sx={{ fontSize: 16 }} />}>
        <Link
          component="button"
          variant="body2"
          underline="hover"
          color="text.secondary"
          onClick={() => navigate('/devdocs')}
          sx={{ cursor: 'pointer' }}
        >
          Dev Insights
        </Link>
        <Typography variant="body2" color="text.primary">
          AIと作る、壊れないバックエンド - 実践編
        </Typography>
      </Breadcrumbs>

      {/* ヒーロー */}
      <Box
        sx={{
          borderRadius: 3,
          p: 4,
          background: (theme) =>
            theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #bf360c 0%, #4e342e 100%)'
              : 'linear-gradient(135deg, #e65100 0%, #bf360c 100%)',
        }}
      >
        <Typography variant="h5" fontWeight={700} sx={{ color: '#fff' }}>
          AIと作る、壊れないバックエンド - 実践編
        </Typography>
        <Typography
          variant="body2"
          sx={{ mt: 0.5, color: 'rgba(255,255,255,0.6)', fontStyle: 'italic' }}
        >
          友達の相談から始まる、初めてのAI駆動開発体験
        </Typography>
        <Typography
          variant="body2"
          sx={{ mt: 1.5, color: 'rgba(255,255,255,0.85)', maxWidth: 620 }}
        >
          チームTODO管理アプリを題材に、環境構築から要件定義・バックエンド実装・フロントエンド開発までをKiroと一緒に進めるハンズオンです。生成AIを使った開発が初めてでも、記事を読みながら手を動かすだけで一通りの流れを体験できます。
        </Typography>
        <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
          <MetaBadge label="16記事" />
          <MetaBadge label="通読 約360分" />
          <MetaBadge label="実践 約8〜12時間" />
        </Stack>
      </Box>

      {/* 前提知識 */}
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
        <Stack spacing={1}>
          <Typography variant="body2" fontWeight={600}>
            前提知識
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            <Chip label="Java / Spring Boot 基礎" size="small" variant="outlined" />
            <Chip label="Docker 基礎" size="small" variant="outlined" />
            <Chip label="Git操作" size="small" variant="outlined" />
            <Chip label="REST APIの概念" size="small" variant="outlined" />
            <Chip label="SQL基礎" size="small" variant="outlined" />
          </Stack>
        </Stack>
      </Box>

      {/* 紹介記事への案内 */}
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <BuildIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
          <Stack spacing={0.25} sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              このハンズオンの背景を知りたい方へ
            </Typography>
            <Typography variant="caption" color="text.secondary">
              紹介記事「AIと作る、壊れないバックエンド」でワークフローの全体像と設計思想を解説しています
            </Typography>
          </Stack>
          <ButtonBase
            onClick={() => navigate('/devdocs/workflow')}
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 1.5,
              border: 1,
              borderColor: 'divider',
              transition: 'border-color 0.2s',
              '&:hover': { borderColor: 'primary.main' },
            }}
          >
            <Typography variant="caption" fontWeight={600} color="primary.main">
              紹介記事を読む
            </Typography>
          </ButtonBase>
        </Stack>
      </Box>

      <Divider />

      {/* シリーズ一覧 */}
      <Stack spacing={2.5}>
        <Typography variant="subtitle1" fontWeight={600}>
          シリーズ一覧
        </Typography>
        {seriesData.map((series) => (
          <SeriesCard key={series.key} series={series} onNavigate={navigate} />
        ))}
      </Stack>
    </Stack>
  );
};

// --- Sub components ---

const MetaBadge: React.FC<{ label: string }> = ({ label }) => (
  <Stack direction="row" spacing={0.5} alignItems="center">
    <AccessTimeIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.7)' }} />
    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
      {label}
    </Typography>
  </Stack>
);

type SeriesCardProps = {
  series: SeriesData;
  onNavigate: (path: string) => void;
};

const SeriesCard: React.FC<SeriesCardProps> = ({ series, onNavigate }) => {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* シリーズヘッダー */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${series.color}14`,
              color: series.color,
            }}
          >
            {React.cloneElement(series.icon as React.ReactElement<{ sx?: object }>, {
              sx: { fontSize: 20 },
            })}
          </Box>
          <Stack spacing={0.25} sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" fontWeight={600}>
                {series.title}
              </Typography>
              <Chip
                label={`${series.articles.length}記事`}
                size="small"
                sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600 }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {series.description}
            </Typography>
          </Stack>
          <Chip
            label={series.target}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 22 }}
          />
        </Stack>
      </Box>

      {/* 記事一覧 */}
      <Box sx={{ p: 1.5 }}>
        <Grid container spacing={1.5}>
          {series.articles.map((article) => (
            <Grid key={article.id} size={{ xs: 12, sm: 6 }}>
              {article.path ? (
                <ButtonBase
                  onClick={() => onNavigate(article.path!)}
                  sx={{
                    width: '100%',
                    display: 'block',
                    textAlign: 'left',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    p: 1.5,
                    transition: 'border-color 0.2s',
                    '&:hover': { borderColor: series.color },
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={article.id}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.6rem',
                          fontWeight: 700,
                          bgcolor: `${series.color}14`,
                          color: series.color,
                        }}
                      />
                      <Typography variant="body2" fontWeight={500}>
                        {article.title}
                      </Typography>
                    </Stack>
                    <ArrowForwardIosIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                  </Stack>
                </ButtonBase>
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    p: 1.5,
                    opacity: 0.5,
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={article.id}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.6rem',
                          fontWeight: 700,
                          bgcolor: `${series.color}14`,
                          color: series.color,
                        }}
                      />
                      <Typography variant="body2" fontWeight={500} color="text.disabled">
                        {article.title}
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              )}
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* 予告コンテンツ（準備中のシリーズ向け） */}
      {series.preview && series.preview.length > 0 && (
        <Box
          sx={{
            px: 2,
            pb: 2,
            pt: 0.5,
          }}
        >
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderLeft: 3,
              borderLeftColor: series.color,
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
              この編で体験すること
            </Typography>
            <Stack spacing={0.75}>
              {series.preview.map((item, idx) => (
                <Stack key={idx} direction="row" spacing={1} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: series.color,
                      mt: 0.8,
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {item}
                  </Typography>
                </Stack>
              ))}
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 2, fontStyle: 'italic' }}
            >
              ご要望がありましたら作成しますので、お気軽にリクエストしてください。
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default HandsonHome;
